import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../_services/auth.service';
import { PUBLIC_PROPERTY_REQUEST } from '../_services/properties/public-property.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(PUBLIC_PROPERTY_REQUEST)) return next(req);
  const authService = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {

      // ✅ 401 — only refresh if NOT the refresh endpoint itself
      if (error.status === 401 && !req.url.includes('/auth/refresh')) {
        return handle401(req, next, authService, router,error);
      }

      // ✅ 403 — only logout if it's a real auth denial, NOT a business rule limit
      if (error.status === 403) {
        const errorCode = error.error?.error;
        const businessRuleCodes = ['VIEW_LIMIT_REACHED']; // add more as needed

        if (!businessRuleCodes.includes(errorCode)) {
          // Real auth failure (e.g. role not allowed) → logout
          authService.clearSession();
        }
        // Business rule limits → just pass the error through to the component
      }

      return throwError(() => error);
    })
  );
};

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  error:any
): Observable<any> {
  if (!authService.isRefreshingToken) {
    authService.setRefreshing(true);
    authService.refreshSubject.next(null);

    return authService.refreshTokens().pipe(
      switchMap((res) => {
        authService.setRefreshing(false);
        return next(
          req.clone({ setHeaders: { Authorization: `Bearer ${res.accessToken}` } })
        );
      }),
      catchError((err) => {
        authService.setRefreshing(false);
        authService.clearSession();
        return throwError(() => error);
      })
    );
  }

  return authService.refreshSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap((token) =>
      next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))
    )
  );
}